import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import element from 'ember-element-helper/helpers/element';
import BvTooltip from 'volta/components/bv-tooltip';
import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';
import cssVars from 'volta/modifiers/css-vars';
import type { SafeString } from '@ember/template';
import type { THue, THueVariationString, TSize } from 'volta/utils/style-utils';
type TBadgeSize = Extract<TSize, 'sm' | 'md' | 'lg'>;
export interface BvBadgeArgs {
    color?: THueVariationString;
    size?: TBadgeSize;
    subtle?: boolean;
    splitColor?: THue;
    outline?: boolean;
    disabled?: boolean;
    onClick?: (event: PointerEvent) => void;
    tooltip?: string;
    text?: string | SafeString;
    tabindex?: `${number}`;
}
interface BvBadgeSignature {
    Args: BvBadgeArgs;
    Blocks: {
        default: [];
    };
    Element: HTMLElement;
}
const Sizes: Set<TBadgeSize> = new Set([
    'sm',
    'md',
    'lg'
]);
export default class BvBadgeComponent extends Component<BvBadgeSignature> {
    get colorClass() {
        const { color } = this.args;
        return color ? `bv-badge--${color}` : undefined;
    }
    get sizeClass() {
        const { size } = this.args;
        return size && Sizes.has(size) ? `bv-badge--${size}` : undefined;
    }
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    {{#let (element (if @onClick 'button' 'span')) as |Tag|}}
      {{! template-lint-disable no-positive-tabindex }}
      <Tag
        role={{if @onClick 'button'}}
        class={{classNames
          'bv-badge'
          this.sizeClass
          (if @subtle 'bv-badge--subtle')
          (if @outline 'bv-badge--outline')
          (if @onClick 'bv-badge--clickable')
          (if @disabled 'bv-badge--disabled')
          (if @splitColor 'bv-badge--split-color')
        }}
        title={{unless @tooltip @text}}
        data-test-bv-badge={{@text}}
        tabindex={{orElse @tabindex (if @onClick '0' '-1')}}
        ...attributes
        {{on 'click' (optional (unless @disabled @onClick))}}
        {{cssVars
          bv-badge-color-rgb=(if
            @color (concat 'var(--bv-color-' @color '-rgb)') 'var(--bv-color-text-muted-rgb)'
          )
          bv-badge-split-color-rgb=(if
            @splitColor
            (concat 'var(--bv-color-' @splitColor '-rgb)')
            'var(--bv-color-text-muted-rgb)'
          )
        }}
      >
        {{#if (has-block)}}
          {{yield}}
        {{else}}
          {{@text}}
        {{/if}}
        {{#if @tooltip}}
          <BvTooltip @text={{@tooltip}} />
        {{/if}}
      </Tag>
    {{/let}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        BvBadge: typeof BvBadgeComponent;
        'bv-badge': typeof BvBadgeComponent;
    }
}
